/**
 * Feature Flags — mirrors apps/experts/public/admin/feature-flags.html, scoped
 * (per the task) to LIST + BOOLEAN TOGGLE. GET /api/admin/feature-flags lists;
 * a boolean flag renders an accessible role="switch" that PATCHes the new value
 * and invalidates the list. Non-boolean flag values render read-only (the full
 * create/edit/delete modal is out of scope for this rebuild slice).
 */
import { cn } from '@andavo/web-ui';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/primitives';
import { useFeatureFlags, useToggleFlag } from '../../data-access/useAdmin';
import type { FeatureFlag } from '../../types';
import { relTime, renderFlagValue } from '../../lib/format';

function FlagToggle({ flag }: { flag: FeatureFlag }) {
  const toggle = useToggleFlag();
  const on = flag.value_json === true;
  return (
    <button
      aria-checked={on}
      aria-label={`Toggle ${flag.name}`}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50',
        on ? 'bg-green-500' : 'bg-content-tertiary/40',
      )}
      disabled={toggle.isPending}
      onClick={() => toggle.mutate({ name: flag.name, value: !on })}
      role="switch"
      type="button"
    >
      <span
        className={cn(
          'inline-block size-4 rounded-full bg-white transition-transform',
          on ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

export function FeatureFlagsPage() {
  const { data, isPending, isError, error, refetch, isFetching } =
    useFeatureFlags();

  return (
    <div>
      <AdminPageHeader
        actions={
          <button
            className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
            onClick={() => void refetch()}
            type="button"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        subtitle={
          data
            ? `${data.total} flag${data.total === 1 ? '' : 's'} · refreshed ${relTime(data.generated_at)}`
            : 'Runtime feature flags'
        }
        title="Feature Flags"
      />

      <Panel>
        {isPending ? <AdminLoading /> : null}
        {isError ? (
          <AdminError
            message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
          />
        ) : null}
        {data && data.flags.length === 0 ? (
          <AdminEmpty>No feature flags defined.</AdminEmpty>
        ) : null}
        {data && data.flags.length > 0 ? (
          <Table
            head={
              <>
                <Th>Name</Th>
                <Th>Value</Th>
                <Th>Description</Th>
                <Th>Updated</Th>
              </>
            }
          >
            {data.flags.map((flag) => (
              <Tr key={flag.name}>
                <Td>
                  <Mono>{flag.name}</Mono>
                </Td>
                <Td>
                  {typeof flag.value_json === 'boolean' ? (
                    <FlagToggle flag={flag} />
                  ) : (
                    <Mono>{renderFlagValue(flag.value_json)}</Mono>
                  )}
                </Td>
                <Td className="text-content-secondary">
                  {flag.description || '—'}
                </Td>
                <Td className="text-content-tertiary text-xs">
                  {relTime(flag.updated_at)}
                </Td>
              </Tr>
            ))}
          </Table>
        ) : null}
      </Panel>
    </div>
  );
}

export default FeatureFlagsPage;
